import { useState, useEffect, useRef } from 'react';

const DEFAULT_THRESHOLD = 64; // px pull distance required to trigger
const PULL_MAX = 96;          // visual cap on the revealed indicator
const RESISTANCE = 0.5;       // resistance factor once past the threshold

/**
 * Pull-to-refresh gesture for a scroll container (touch + mouse).
 *
 * Attaches native listeners to the element behind `scrollRef` and reports a
 * downward pull when that element is already scrolled to the very top (nothing
 * above it). The pull distance drives a visual indicator, and releasing past
 * `threshold` fires `onRefresh` once.
 *
 * Touch (mobile rubber-band) and mouse-drag (desktop) are both supported.
 *
 * @param {Object} opts
 * @param {React.Ref} opts.scrollRef    - ref to the scrollable element
 * @param {Function}  opts.onRefresh    - called when a qualifying pull releases
 * @param {boolean}   [opts.enabled]    - disable the gesture (default true)
 * @param {number}    [opts.threshold]  - px pull distance to trigger (default 64)
 * @returns {{ pull: number, phase: 'idle'|'pulling'|'ready' }}
 */
export default function usePullToRefresh({
  scrollRef,
  onRefresh,
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
}) {
  const [pull, setPull] = useState(0);
  const [phase, setPhase] = useState('idle');

  // Live values kept in refs so the native listeners stay stable across renders.
  const pullRef = useRef(0);
  const touchRef = useRef({ startY: 0, atTop: false, pulling: false });
  const mouseRef = useRef({ active: false, startY: 0, atTop: false, pulling: false });
  const wheelRef = useRef({ accum: 0, cooldownUntil: 0 });
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const setPullValue = (val) => {
      pullRef.current = val;
      setPull(val);
    };

    // Advance the pull distance for a gesture at the given pointer Y.
    const movePull = (g, clientY) => {
      const dy = clientY - g.startY;

      // Dragging back up / not pulling — clear any partial pull.
      if (dy <= 0) {
        if (g.pulling) {
          g.pulling = false;
          setPullValue(0);
          setPhase('idle');
        }
        return;
      }

      g.pulling = true;
      const t = thresholdRef.current;
      const resisted = dy <= t ? dy : t + (dy - t) * RESISTANCE;
      const val = Math.min(resisted, PULL_MAX);
      setPullValue(val);
      setPhase(val >= t ? 'ready' : 'pulling');
    };

    // Resolve the gesture — fire onRefresh if it qualified, then reset.
    const endPull = (g) => {
      const shouldRefresh = g.pulling && pullRef.current >= thresholdRef.current;
      g.pulling = false;
      setPullValue(0);
      setPhase('idle');
      if (shouldRefresh) onRefreshRef.current?.();
    };

    // ── Touch (mobile) ─────────────────────────────────────────
    const onTouchStart = (e) => {
      if (!enabledRef.current) return;
      const touch = e.touches[0];
      touchRef.current = {
        startY: touch.clientY,
        atTop: el.scrollTop <= 0,
        pulling: false,
      };
    };

    const onTouchMove = (e) => {
      if (!enabledRef.current) return;
      const g = touchRef.current;
      if (!g.atTop) return;
      // Downward drag at the top — take over native scroll.
      if (e.touches[0].clientY - g.startY > 0 && e.cancelable) e.preventDefault();
      movePull(g, e.touches[0].clientY);
    };

    const onTouchEnd = () => endPull(touchRef.current);

    // ── Mouse (desktop) ────────────────────────────────────────
    const onMouseDown = (e) => {
      if (!enabledRef.current) return;
      if (e.button !== 0) return; // primary button only
      const atTop = el.scrollTop <= 0;
      if (atTop) e.preventDefault(); // prevent text selection during the pull
      mouseRef.current = { active: true, startY: e.clientY, atTop, pulling: false };
    };

    const onMouseMove = (e) => {
      const g = mouseRef.current;
      if (!g.active || !g.atTop) return;
      if (e.clientY - g.startY > 0) e.preventDefault();
      movePull(g, e.clientY);
    };

    const onMouseUp = () => {
      const g = mouseRef.current;
      if (!g.active) return;
      g.active = false;
      endPull(g);
    };

    // ── Wheel / trackpad overscroll (desktop fallback) ──────────
    // When the list is already at the top and the user keeps scrolling up,
    // accumulate the overscroll and fire onRefresh once past the threshold.
    const onWheel = (e) => {
      if (!enabledRef.current) return;

      // Scrolled away from the top, or scrolling down — reset accumulation.
      if (el.scrollTop > 0 || e.deltaY >= 0) {
        wheelRef.current.accum = 0;
        return;
      }

      const now = performance.now();
      if (now < wheelRef.current.cooldownUntil) return;

      wheelRef.current.accum += -e.deltaY;
      if (wheelRef.current.accum >= thresholdRef.current) {
        wheelRef.current.accum = 0;
        wheelRef.current.cooldownUntil = now + 1200;
        onRefreshRef.current?.();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    el.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    el.addEventListener('wheel', onWheel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('wheel', onWheel);
    };
  }, [scrollRef]);

  return { pull, phase };
}
